import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiTruck,
  FiPrinter,
  FiTrendingUp,
  FiPackage,
  FiDollarSign,
  FiShoppingCart,
  FiFileText,
  FiRefreshCcw,
  FiSearch,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';
import ClientPagination, { useClientPagination } from '../../components/common/ClientPagination';

const COLORS = ["#7A2238", "#D4AF37", "#5A182A", "#A94D6D", "#2563EB"];

const today = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const number = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

const comparison = (value, inverse = false) => {
  if (value === null || value === undefined) {
    return { text: "No data last year", className: "bg-gray-100 text-gray-500" };
  }

  const percentage = Number(value);
  const positive = inverse ? percentage <= 0 : percentage >= 0;

  return {
    text: `${percentage >= 0 ? "+" : ""}${percentage.toFixed(1)}% vs last year`,
    className: positive
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700",
  };
};

const label = (date) =>
  date
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
      }).format(new Date(`${date}T00:00:00.000Z`))
    : "";

export default function PurchaseReports() {
  const { accessToken } = useAuth();
  const currentDate = today();

  const [dateFrom, setDateFrom] = useState(`${currentDate.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(currentDate);
  const [storeId, setStoreId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [search, setSearch] = useState("");

  const [report, setReport] = useState(null);
  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadFilters = useCallback(async () => {
    if (!accessToken) return;

    try {
      const [storeData, supplierData, brandData] = await Promise.all([
        apiRequest("/stores", { accessToken }),
        apiRequest("/suppliers", { accessToken }),
        apiRequest("/brands", { accessToken }),
      ]);

      setStores(storeData || []);
      setSuppliers(supplierData || []);
      setBrands(brandData || []);
    } catch {
      // The report can still load if optional dropdown data fails.
    }
  }, [accessToken]);

  const loadReport = useCallback(
    async (refresh = false) => {
      if (!accessToken) return;

      try {
        refresh ? setRefreshing(true) : setLoading(true);
        setError("");

        const query = new URLSearchParams({
          from: dateFrom,
          to: dateTo,
        });

        if (storeId) query.set("storeId", storeId);
        if (supplierId) query.set("supplierId", supplierId);
        if (brandId) query.set("brandId", brandId);

        const response = await apiRequest(
          `/reports/purchases?${query.toString()}`,
          { accessToken },
        );

        setReport(response);
      } catch (requestError) {
        setError(requestError.message || "Unable to load purchase report.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accessToken, dateFrom, dateTo, storeId, supplierId, brandId],
  );

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const metrics = report?.metrics || {};
  const changes = report?.comparison?.changes || {};

  const chartData = useMemo(() => {
    const previous = report?.previousPurchaseTrend || [];

    return (report?.purchaseTrend || []).map((item, index) => ({
      label: label(item.date),
      current: Number(item.amount || 0),
      previous: Number(previous[index]?.amount || 0),
    }));
  }, [report]);

  const purchases = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (report?.purchases || []).filter((item) => {
      if (!query) return true;

      return (
        item.poNo?.toLowerCase().includes(query) ||
        item.grn?.toLowerCase().includes(query) ||
        item.supplier?.toLowerCase().includes(query) ||
        item.product?.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query)
      );
    });
  }, [report?.purchases, search]);

  const cards = [
    {
      title: "Purchase Orders",
      value: number(metrics.purchaseOrders),
      compare: comparison(changes.purchaseOrders),
      icon: <FiShoppingCart size={24} />,
      color: "bg-blue-50 text-blue-600",
    },
    {
      title: "Items Purchased",
      value: number(metrics.itemsPurchased),
      compare: comparison(changes.itemsPurchased),
      icon: <FiPackage size={24} />,
      color: "bg-green-50 text-green-600",
    },
    {
      title: "Purchase Value",
      value: money(metrics.purchaseValue),
      compare: comparison(changes.purchaseValue),
      icon: <FiDollarSign size={24} />,
      color: "bg-purple-50 text-purple-700",
    },
    {
      title: "Suppliers",
      value: number(metrics.suppliers),
      compare: comparison(changes.suppliers),
      icon: <FiTruck size={24} />,
      color: "bg-pink-50 text-pink-600",
    },
    {
      title: "Average PO",
      value: money(metrics.averagePo),
      compare: comparison(changes.averagePo),
      icon: <FiTrendingUp size={24} />,
      color: "bg-yellow-50 text-yellow-700",
    },
    {
      title: "Pending GRN",
      value: number(metrics.pendingGrns),
      compare: comparison(changes.pendingGrns, true),
      icon: <FiFileText size={24} />,
      color: "bg-red-50 text-red-600",
    },
  ];


  const pagination = useClientPagination(purchases);
  useEffect(() => { pagination.setPage(1); }, [search]);
  return (
    <div className="charms-analytics space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-primary-800">
            Purchase Analytics
          </h1>
          <p className="mt-2 text-gray-500">
            Purchase orders, GRNs, suppliers, and last-year comparisons.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="rounded-xl border bg-white px-5 py-3"
          >
            <FiPrinter />
          </button>

          <button
            onClick={() => loadReport(true)}
            disabled={refreshing}
            className="flex items-center gap-2 rounded-xl border bg-white px-5 py-3 disabled:opacity-50"
          >
            <FiRefreshCcw className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>

        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(event) => setDateFrom(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          />

          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={currentDate}
            onChange={(event) => setDateTo(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          />

          <select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Stores</option>
            {stores
              .filter((store) => store.isActive)
              .map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name} ({store.code})
                </option>
              ))}
          </select>

          <select
            value={supplierId}
            onChange={(event) => setSupplierId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Suppliers</option>
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.displayName || supplier.name}
              </option>
            ))}
          </select>

          <select
            value={brandId}
            onChange={(event) => setBrandId(event.target.value)}
            className="rounded-xl border border-gray-200 p-3"
          >
            <option value="">All Brands</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="rounded-3xl bg-white py-24 text-center text-gray-500 shadow-sm">
          Loading purchase report...
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {cards.map((card) => (
              <div key={card.title} className="analytics-kpi">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{card.title}</p>
                    <h3 className="mt-2 text-3xl font-bold">{card.value}</h3>
                    <span
                      className={`mt-3 inline-block rounded-full px-3 py-1 text-xs font-semibold ${card.compare.className}`}
                    >
                      {card.compare.text}
                    </span>
                  </div>

                  <div className="analytics-icon flex items-center justify-center">
                    {card.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <section className="xl:col-span-7 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-2 text-xl font-bold">Purchase Trend Comparison</h2>
              <p className="mb-5 text-sm text-gray-500">
                Posted GRN value: selected period versus last year.
              </p>

              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip formatter={(value) => money(value)} />
                  <Legend />

                  <Line
                    type="monotone"
                    dataKey="current"
                    name="This Year"
                    stroke="#7A2238"
                    strokeWidth={3}
                  />

                  <Line
                    type="monotone"
                    dataKey="previous"
                    name="Last Year"
                    stroke="#D4AF37"
                    strokeWidth={3}
                    strokeDasharray="7 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </section>

            <section className="xl:col-span-5 rounded-3xl bg-white p-6 shadow-sm">
              <h2 className="mb-5 text-xl font-bold">Supplier-wise Purchases</h2>

              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={report.supplierPurchases || []}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                  >
                    {(report.supplierPurchases || []).map((item, index) => (
                      <Cell
                        key={`${item.name}-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>

                  <Tooltip formatter={(value) => money(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </section>
          </div>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-xl font-bold">Brand-wise Purchases</h2>

            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={report.brandPurchases || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => money(value)} />
                <Bar
                  dataKey="amount"
                  fill="#7A2238"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </section>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold">Purchase Details</h2>
                <p className="text-gray-500">Posted goods-receipt summary</p>
              </div>

              <div className="relative w-full lg:w-80">
                <FiSearch className="absolute left-4 top-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search PO, GRN, supplier or SKU"
                  className="w-full rounded-xl border border-gray-200 py-3 pl-11 pr-4"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[1400px]">
                <thead className="bg-[#F8F6F5]">
                  <tr>
                    {[
                      "PO Number",
                      "GRN",
                      "Supplier",
                      "Brand",
                      "Product",
                      "SKU",
                      "Ordered",
                      "Received",
                      "Pending",
                      "Rate",
                      "Amount",
                    ].map((heading) => (
                      <th key={heading} className="px-4 py-3 text-left">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {purchases.length ? (
                    pagination.pageItems.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-4">{item.poNo}</td>
                        <td className="px-4 py-4">{item.grn}</td>
                        <td className="px-4 py-4">{item.supplier}</td>
                        <td className="px-4 py-4">{item.brand}</td>
                        <td className="px-4 py-4 font-semibold">{item.product}</td>
                        <td className="px-4 py-4">{item.sku}</td>
                        <td className="px-4 py-4 text-center">{number(item.qty)}</td>
                        <td className="px-4 py-4 text-center text-green-600">{number(item.received)}</td>
                        <td className="px-4 py-4 text-center text-orange-600">{number(item.pending)}</td>
                        <td className="px-4 py-4 text-right">{money(item.rate)}</td>
                        <td className="px-4 py-4 text-right font-semibold">{money(item.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="py-16 text-center text-gray-400">
                        No purchase data found for this selection.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <ClientPagination pagination={pagination} label="purchase rows" />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
